import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import Swal from "sweetalert2";
import pluralize from "pluralize";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { Layout, Prefill } from "../../../types/GlobalInterfaces";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import { newToastEvent } from "../../../utils/ToastUtil";
import TenantAPI from "../../api/TenantAPI";

interface MutationProps {
  global: Layout | Prefill;
  globalsType: "QuickCreates" | "Layouts" | "Settings" | "Tags" | "Defaults";
  onSuccess?: (newGlobal: any, previousGlobal: any) => void;
}

export const convertToSingular = (word: string) => {
  return pluralize.singular(word.toLowerCase());
};

const useCreateGlobal = () => {
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);

  const createGlobal = useMutation({
    mutationFn: async ({
      global,
      globalsType: moduleAPIName,
    }: MutationProps) => {
      return TenantAPI.createGlobal(moduleAPIName, global);
    },
    onSuccess: (response, { global, globalsType, onSuccess }) => {
      const newGlobal = response.data.data.global;

      queryClient.setQueryData([globalsType, tenantID], (prev: any) => {
        if (!Array.isArray(prev)) return prev;

        return [...prev, newGlobal];
      });

      Swal.fire({
        title: "Success",
        text: `Successfully added to Global ${convertToSingular(globalsType)}.`,
        icon: "success",
        confirmButtonColor: "var(--primary)",
      });

      newToastEvent(
        "success",
        `Successfully created ${convertToSingular(globalsType)} "${
          newGlobal.Name
        }".`
      );

      if (onSuccess) {
        callFunctionIfExists(onSuccess(newGlobal, global));
      }
    },
    onError: (err: any) => {
      Swal.fire({
        title: "Error",
        text: err.response.data.message,
        icon: "error",
      });
      newToastEvent("error", err.response.data.message);
    },
  });

  return {
    createGlobal: createGlobal.mutate,
    createGlobalLoading: createGlobal.isPending,
    createGlobalError: createGlobal?.isError,
  };
};

export default useCreateGlobal;
